// Models
import { FormFieldSchema, FormFieldArraySchema } from 'app/shared/models/form';
import { U21FormFieldTypes } from 'app/shared/u21-ui/components';

// Utils
import { required } from 'app/shared/utils/validation';

// Constants
import { GENDER_TYPE_OPTIONS } from 'app/modules/goAML/schemas/catalogues/genderTypes';
import { COUNTRY_CODE_OPTIONS } from 'app/modules/goAML/schemas/catalogues/countryCode';
import { T_PHONE_SCHEMA } from 'app/modules/goAML/schemas/tPhoneSchema';
import { T_PERSON_IDENTIFICATION_SCHEMA } from 'app/modules/goAML/schemas/tPersonIdentification';
import { T_ADDRESS_SCHEMA } from 'app/modules/goAML/schemas/tAddressSchema';

export const GENDER_FIELD: FormFieldSchema<string> = {
  label: 'Gender',
  name: 'gender',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: GENDER_TYPE_OPTIONS,
  },
};

export const TITLE_FIELD: FormFieldSchema<string> = {
  label: 'Title',
  name: 'title',
  type: U21FormFieldTypes.TEXT,
};

export const FIRST_NAME_FIELD: FormFieldSchema<string> = {
  label: 'First name',
  name: 'first_name',
  required: true,
  type: U21FormFieldTypes.TEXT,
  validate: required('First name is required.'),
};

export const MIDDLE_NAME_FIELD: FormFieldSchema<string> = {
  label: 'Middle name',
  name: 'middle_name',
  type: U21FormFieldTypes.TEXT,
};

export const LAST_NAME_FIELD: FormFieldSchema<string> = {
  label: 'Last name',
  name: 'last_name',
  type: U21FormFieldTypes.TEXT,
  required: true,
  validate: required('Last name is required.'),
};

export const BIRTHDATE_FIELD: FormFieldSchema<string> = {
  label: 'Birth date',
  name: 'birthdate',
  type: U21FormFieldTypes.DATE_PICKER,
};

export const BIRTH_PLACE_FIELD: FormFieldSchema<string> = {
  label: 'Birth place',
  name: 'birth_place',
  type: U21FormFieldTypes.TEXT,
};

export const MOTHERS_NAME_FIELD: FormFieldSchema<string> = {
  label: "Mother's name",
  name: 'mothers_name',
  type: U21FormFieldTypes.TEXT,
};

export const ALIAS_FIELD: FormFieldSchema<string> = {
  label: 'Alias',
  name: 'alias',
  type: U21FormFieldTypes.TEXT,
};

export const SSN_FIELD: FormFieldSchema<string> = {
  label: 'SSN',
  name: 'ssn',
  type: U21FormFieldTypes.TEXT,
};

export const PASSPORT_NUMBER_FIELD: FormFieldSchema<string> = {
  label: 'Passport number',
  name: 'passport_number',
  type: U21FormFieldTypes.TEXT,
};

export const PASSPORT_COUNTRY: FormFieldSchema<string> = {
  label: 'Passport country',
  name: 'passport_country',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: COUNTRY_CODE_OPTIONS,
  },
};

export const ID_NUMBER_FIELD: FormFieldSchema<string> = {
  label: 'ID number',
  name: 'id_number',
  type: U21FormFieldTypes.TEXT,
};

export const NATIONALITY1_FIELD: FormFieldSchema<string> = {
  label: 'Nationality 1',
  name: 'nationality1',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: COUNTRY_CODE_OPTIONS,
  },
};

export const NATIONALITY2_FIELD: FormFieldSchema<string> = {
  label: 'Nationality 2',
  name: 'nationality2',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: COUNTRY_CODE_OPTIONS,
  },
};

export const NATIONALITY3_FIELD: FormFieldSchema<string> = {
  label: 'Nationality 3',
  name: 'nationality3',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: COUNTRY_CODE_OPTIONS,
  },
};

export const RESIDENCE_FIELD: FormFieldSchema<string> = {
  label: 'Residence',
  name: 'residence',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: COUNTRY_CODE_OPTIONS,
  },
};

export const EMAIL_FIELD: FormFieldSchema<string> = {
  label: 'Email',
  name: 'email',
  type: U21FormFieldTypes.TEXT,
};

export const OCCUPATION_FIELD: FormFieldSchema<string> = {
  label: 'Occupation',
  name: 'occupation',
  type: U21FormFieldTypes.TEXT,
};

export const EMPLOYER_NAME_FIELD: FormFieldSchema<string> = {
  label: 'Employer name',
  name: 'employer_name',
  type: U21FormFieldTypes.TEXT,
};

// TODO add employer_address_id
// TODO add employer_phone_id

export const IS_DECEASED_FIELD: FormFieldSchema<string> = {
  label: 'Deceased?',
  name: 'deceased',
  type: U21FormFieldTypes.SELECT,
  fieldProps: {
    options: [
      {
        value: '0',
        text: 'No',
      },
      {
        value: '1',
        text: 'Yes',
      },
    ],
  },
};

export const DATE_DECEASED_FIELD: FormFieldSchema<string> = {
  label: 'Date deceased',
  name: 'date_deceased',
  type: U21FormFieldTypes.DATE_PICKER,
};

export const TAX_NUMBER_FIELD: FormFieldSchema<string> = {
  label: 'Tax number',
  name: 'tax_number',
  type: U21FormFieldTypes.TEXT,
};

export const TAX_REG_NUMBER_FIELD: FormFieldSchema<string> = {
  label: 'Tax registration number',
  name: 'tax_reg_number',
  type: U21FormFieldTypes.TEXT,
};

export const SOURCE_OF_WEALTH_FIELD: FormFieldSchema<string> = {
  label: 'Source of wealth',
  name: 'source_of_wealth',
  type: U21FormFieldTypes.TEXT,
};

export const COMMENTS_FIELD: FormFieldSchema<string> = {
  label: 'Comments',
  name: 'comments',
  type: U21FormFieldTypes.TEXTAREA,
};

export const PHONES_FIELD: FormFieldArraySchema<any> = {
  name: 'phones',
  label: 'Phones',
  fields: [
    ...T_PHONE_SCHEMA.map((config) => ({
      ...config,
      name: `phone.${config.name}`,
    })),
  ],
  type: 'ARRAY',
};

export const ADDRESSES_FIELD: FormFieldArraySchema<any> = {
  name: 'addresses',
  label: 'Addresses',
  fields: [
    ...T_ADDRESS_SCHEMA.map((config) => ({
      ...config,
      name: `address.${config.name}`,
    })),
  ],
  type: 'ARRAY',
};

export const IDENTIFICATIONS_FIELD: FormFieldArraySchema<any> = {
  name: 'identification',
  label: 'Identifications',
  fields: [...T_PERSON_IDENTIFICATION_SCHEMA],
  type: 'ARRAY',
};
